import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  IconCheck,
  IconChevronDown,
  IconList,
  IconX,
} from "@/components/icons";
import { useClickOutside } from "@/hooks/useClickOutside";
import type { FormBuilderField } from "@/types/formBuilder";

function fieldLabel(field: FormBuilderField): string {
  return field.label.trim() || field.name || `Поле #${field.id}`;
}

interface SubmissionsGroupBySelectProps {
  fields: FormBuilderField[];
  value: number | null;
  onChange: (fieldId: number | null) => void;
}

export function SubmissionsGroupBySelect({
  fields,
  value,
  onChange,
}: SubmissionsGroupBySelectProps) {
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useClickOutside(containerRef, close, open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close, open]);

  if (fields.length === 0) return null;

  const selected = fields.find((field) => field.id === value);
  const grouped = selected != null;

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center">
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={menuId}
          aria-label="Группировать по"
          onClick={() => setOpen((current) => !current)}
          className={[
            "inline-flex h-8 max-w-[16rem] items-center gap-1.5 rounded-lg px-2.5 font-onest text-xs font-medium",
            grouped
              ? "bg-brand-50 text-brand-800 hover:bg-brand-100"
              : "bg-slate-100 text-slate-700 hover:bg-slate-200/80",
            grouped ? "rounded-r-none" : "",
          ].join(" ")}
        >
          <IconList size={14} className="shrink-0 opacity-70" />
          <span className="truncate">
            {grouped ? fieldLabel(selected) : "Группировать"}
          </span>
          <IconChevronDown
            size={14}
            className={[
              "shrink-0 opacity-60 transition-transform",
              open ? "rotate-180" : "",
            ].join(" ")}
          />
        </button>
        {grouped ? (
          <button
            type="button"
            title="Сбросить группировку"
            aria-label="Сбросить группировку"
            onClick={() => onChange(null)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-r-lg bg-brand-50 text-brand-700 hover:bg-brand-100"
          >
            <IconX size={14} />
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          id={menuId}
          role="listbox"
          aria-label="Поле группировки"
          className="absolute right-0 z-40 mt-1.5 w-[min(18rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="option"
            aria-selected={!grouped}
            onClick={() => {
              onChange(null);
              close();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left font-onest text-sm text-slate-700 hover:bg-slate-50"
          >
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              {!grouped ? (
                <IconCheck size={14} className="text-brand-600" />
              ) : null}
            </span>
            Без группировки
          </button>
          <div className="my-1 border-t border-slate-100" />
          <div className="max-h-56 overflow-y-auto">
            {fields.map((field) => {
              if (field.id == null) return null;
              const active = field.id === value;
              return (
                <button
                  key={field.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  title={fieldLabel(field)}
                  onClick={() => {
                    onChange(field.id!);
                    close();
                  }}
                  className={[
                    "flex w-full items-center gap-2 px-3 py-2 text-left font-onest text-sm hover:bg-slate-50",
                    active ? "font-medium text-brand-800" : "text-slate-800",
                  ].join(" ")}
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    {active ? (
                      <IconCheck size={14} className="text-brand-600" />
                    ) : null}
                  </span>
                  <span className="min-w-0 truncate">{fieldLabel(field)}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
